import React, { createContext, useState, useContext, useRef } from 'react';
import { ToastProviderProps, WarningToastContextProps } from '@helpers/Models';
import { WarningToast } from '@components/Toasts/WarningToast';

const WarningToastContext = createContext<WarningToastContextProps | undefined>(
  undefined,
);

export const WarningToastProvider: React.FC<ToastProviderProps> = ({
  children,
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastTitle, setToastTitle] = useState('');
  const onFinishRef = useRef<(() => void) | null>(null);

  /**
   * Displays a confirmation toast with a message, title, and an optional callback function
   * to execute when the user confirms the action.
   *
   * @param {string} message - The message to display in the toast.
   * @param {string} title - The title of the toast.
   * @param {() => void} [onFinish] - (Optional) A callback function that executes when the user confirms the action.
   *
   */
  const showWarningToast = (
    message: string,
    title: string,
    onFinish?: () => void,
  ) => {
    setToastMessage(message);
    setToastTitle(title);
    onFinishRef.current = onFinish || null;
    setShowToast(true);
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  return (
    <WarningToastContext.Provider value={{ showWarningToast }}>
      {children}
      {showToast && (
        <WarningToast
          title={toastTitle}
          message={toastMessage}
          onClose={handleCloseToast}
          onFinish={() => {
            if (onFinishRef.current) {
              onFinishRef.current();
            }
            handleCloseToast();
          }}
        />
      )}
    </WarningToastContext.Provider>
  );
};

export const UseWarningToast = (): WarningToastContextProps => {
  const context = useContext(WarningToastContext);
  if (!context) {
    throw new Error('WarningToast must be used within a WarningToastProvider');
  }
  return context;
};
